/**
 * Utility functions for parsing Bookshop.org affiliate URLs.
 *
 * Affiliate URL format: https://bookshop.org/a/{affiliateId}/{sku}
 */

/**
 * Regex pattern for Bookshop affiliate URLs.
 * Capture groups:
 *   [1] affiliateId - characters between /a/ and the next /
 *   [2] sku - characters after affiliateId until query/hash/end
 *
 * Compiled at module load for performance.
 */
const BOOKSHOP_AFFILIATE_REGEX =
  /^https?:\/\/bookshop\.org\/a\/([^/]+)\/([^/?#]+)/;

export interface AffiliateInfo {
  affiliateId: string;
  sku: string;
  originalUrl: string;
}

/**
 * Parse a Bookshop.org affiliate URL to extract affiliateId and sku.
 *
 * @param url - The URL to parse
 * @returns AffiliateInfo if valid affiliate URL, null otherwise
 *
 * @example
 * parseBookshopAffiliateUrl("https://bookshop.org/a/12345/9780123456789")
 * // Returns: { affiliateId: "12345", sku: "9780123456789", originalUrl: "..." }
 */
export function parseBookshopAffiliateUrl(url: string): AffiliateInfo | null {
  if (!url) return null;

  const match = BOOKSHOP_AFFILIATE_REGEX.exec(url);
  if (!match) return null;

  // After a successful exec(), capture groups [1] and [2] are guaranteed to be
  // strings (not null/undefined) because the regex requires these segments.
  // TypeScript cannot infer this from RegExp types, so we validate explicitly.
  const affiliateId = match[1];
  const sku = match[2];
  if (!affiliateId || !sku) return null;

  return { affiliateId, sku, originalUrl: url };
}

/**
 * Check if a URL is a valid Bookshop.org affiliate URL
 * @param url The URL to check
 * @returns true if valid affiliate URL
 */
export function isBookshopAffiliateUrl(url: string): boolean {
  if (!url) return false;
  return BOOKSHOP_AFFILIATE_REGEX.test(url);
}
